import { ApiError } from '../api';

/**
 * The only console in the application.
 *
 * `no-console` is an error everywhere else, which is not stylistic. Console
 * output is read by anyone with the tab open, is captured by session-recording
 * and error-reporting tools, and outlives the page in a browser's own log. A
 * `console.error(response)` on a screen listing insured people puts names,
 * CPFs and dates of birth into all three.
 *
 * So what goes out is deliberately thin: what kind of failure it was, the HTTP
 * status, the stable error code and the correlation id. The correlation id is
 * the whole point -- the backend attaches it to every log line for that
 * request, so a report of "it broke, reference 9f8a7b6c" is enough for someone
 * to find the traceback on the server, where the details are allowed to live.
 */
export interface FailureReport {
  readonly where: string;
  readonly kind: string;
  readonly status?: number | undefined;
  readonly code?: string | undefined;
  readonly correlationId?: string | undefined;
}

export function describeFailure(where: string, error: unknown): FailureReport {
  if (error instanceof ApiError) {
    return {
      where,
      kind: error.kind,
      status: error.status,
      code: error.code,
      correlationId: error.correlationId,
    };
  }
  return { where, kind: error instanceof Error ? error.name : 'unknown' };
}

export function reportFailure(where: string, error: unknown): void {
  // Codes, not payloads -- see the note above. `no-console` is switched off for
  // this directory alone, in eslint.config.js.
  console.error('i8life.failure', describeFailure(where, error));
}
